import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth, db } from './firebaseConfig';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { BarChart, Activity, Users, Bot } from 'lucide-react';
import './LinkedInTheme.css';

function AgentPerformance() {
  const { t } = useTranslation();
  const { userProfile } = useOutletContext();
  const [user] = useState(auth.currentUser);
  const [totalConversations, setTotalConversations] = useState(0);
  const [screeningsPassed, setScreeningsPassed] = useState(0);
  const [totalTwins, setTotalTwins] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fallback avatar
  const defaultAvatar = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iI2NjYyI+PHBhdGggZD0iTTEyIDEyYzIuMjEgMC00LTEuNzktNC00czEuNzktNCA0LTRzNCAxLjc5IDQgNHMtMS43OSA0LTQgNHptMCAyYy0yLjY3IDAtOCAxLjM0LTggNHYyaDE2di0yYy04LTIuNjYtNS4zMy00LTgtNHoiLz48L3N2Zz4=";

  useEffect(() => {
    const fetchData = async () => {
      if (user) {
        try {
            // Fetch ALL VTC Data to aggregate chat counts
            const qVtc = query(collection(db, 'talentCards'), where('userId', '==', user.uid));
            const vtcSnap = await getDocs(qVtc);
            setTotalTwins(vtcSnap.size);
            let chats = 0;
            vtcSnap.forEach(doc => {
                chats += (doc.data().chatCount || 0);
            });
            setTotalConversations(chats);

            // Fetch Simulations for Screenings Passed
            const qSims = query(collection(db, 'simulations'), where('participant_uids', 'array-contains', user.uid));
            const simSnap = await getDocs(qSims);
            let passedCount = 0;
            simSnap.forEach(doc => {
                const simData = doc.data();
                if (simData.candidates) {
                    Object.values(simData.candidates).forEach(candidate => {
                        if (candidate.userId === user.uid && candidate.status === 'shortlisted') {
                            passedCount++;
                        }
                    });
                }
            });
            setScreeningsPassed(passedCount);

        } catch (e) {
            console.error("Error fetching data", e);
        }
      }
      setLoading(false);
    };
    fetchData();
  }, [user]);

  return (
    <div className="li-layout">
      <div className="li-container">
         <aside className="li-sidebar-left">
            {/* Profile Card */}
             <div className="li-card li-profile-card">
                <div className="li-card-background" style={{ backgroundColor: '#a0b4b7' }}></div>
                <div className="li-card-content">
                    <img 
                        src={userProfile?.avatar_url || defaultAvatar} 
                        alt="Profile" 
                        className="li-avatar"
                    />
                    <div className="li-identity">
                        <h3 className="li-name">{userProfile?.firstName} {userProfile?.lastName}</h3>
                         <p className="li-headline">{t('agentPerformance.analyticsTitle', 'Agent Performance Analytics')}</p>
                    </div>
                </div>
             </div>
         </aside>
         
         <main className="li-main-feed">
            <div className="li-card li-feed-post">
                <div className="li-post-header">
                     <div className="li-post-actor-image" style={{ backgroundColor: '#0073b1', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BarChart size={24} />
                     </div>
                     <div className="li-post-meta">
                        <span className="li-post-title">{t('agentPerformance.activityOverview', 'Agent Activity Overview')}</span>
                        <span className="li-post-subtitle">{t('agentPerformance.realTimeStats', 'Real-time statistics for your AI Twin')}</span>
                     </div>
                </div>
                <div className="li-post-content">
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '20px' }}>
                        <div style={{ flex: 1, minWidth: '150px', padding: '20px', backgroundColor: '#f3f2ef', borderRadius: '8px', textAlign: 'center' }}>
                             <Bot size={32} style={{ color: '#666', marginBottom: '10px' }} />
                             <h2 style={{ margin: 0, fontSize: '32px', fontWeight: '600' }}>{totalTwins}</h2>
                             <p style={{ margin: '5px 0 0', color: '#666', fontSize: '14px' }}>{t('agentPerformance.totalTwins', 'Total AI Twins')}</p>
                        </div>
                        <div style={{ flex: 1, minWidth: '150px', padding: '20px', backgroundColor: '#f3f2ef', borderRadius: '8px', textAlign: 'center' }}>
                             <Users size={32} style={{ color: '#0073b1', marginBottom: '10px' }} />
                             <h2 style={{ margin: 0, fontSize: '32px', fontWeight: '600' }}>{totalConversations}</h2>
                             <p style={{ margin: '5px 0 0', color: '#666', fontSize: '14px' }}>{t('agentPerformance.totalConversations', 'Total Conversations')}</p>
                        </div>
                        <div style={{ flex: 1, minWidth: '150px', padding: '20px', backgroundColor: '#f3f2ef', borderRadius: '8px', textAlign: 'center' }}>
                             <Activity size={32} style={{ color: '#057642', marginBottom: '10px' }} />
                             <h2 style={{ margin: 0, fontSize: '32px', fontWeight: '600' }}>{screeningsPassed}</h2>
                             <p style={{ margin: '5px 0 0', color: '#666', fontSize: '14px' }}>{t('agentPerformance.screeningsPassed', 'Screenings Passed')}</p>
                        </div>
                    </div>
                    
                    <div style={{ marginTop: '30px', borderTop: '1px solid #e0e0e0', paddingTop: '20px' }}>
                        <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px' }}>{t('agentPerformance.benefitsTitle', 'Benefits of your AI Twin')}</h4>
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            <li style={{ display: 'flex', alignItems: 'start', marginBottom: '15px' }}>
                                <span style={{ marginRight: '10px', color: '#057642', fontWeight: 'bold' }}>✔</span>
                                <span><strong>{t('agentPerformance.availability', '24/7 Availability:')}</strong> {t('agentPerformance.availabilityDesc', 'Your agent engages with recruiters and hiring managers anytime, anywhere.')}</span>
                            </li>
                             <li style={{ display: 'flex', alignItems: 'start', marginBottom: '15px' }}>
                                <span style={{ marginRight: '10px', color: '#057642', fontWeight: 'bold' }}>✔</span>
                                <span><strong>{t('agentPerformance.instantScreening', 'Instant Screening:')}</strong> {t('agentPerformance.instantScreeningDesc', 'Your twin participates in AI-to-AI interviews to qualify you for roles instantly.')}</span>
                            </li>
                             <li style={{ display: 'flex', alignItems: 'start' }}>
                                <span style={{ marginRight: '10px', color: '#057642', fontWeight: 'bold' }}>✔</span>
                                <span><strong>{t('agentPerformance.consistentRepresentation', 'Consistent Representation:')}</strong> {t('agentPerformance.consistentRepresentationDesc', 'Always presents your best professional self based on your AI Twin.')}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
         </main>
         
         <aside className="li-sidebar-right">
             <div className="li-card li-news-card">
                <div className="li-card-header">
                    <h3>{t('agentPerformance.quickActions', 'Quick Actions')}</h3>
                </div>
                <div className="li-card-body">
                     <Link to="/candidate-interviews" className="li-btn-outline-primary" style={{ width: '100%', textAlign: 'center', display: 'block', marginBottom: '10px', boxSizing: 'border-box' }}>
                        {t('agentPerformance.viewReports', 'View Interview Reports')}
                     </Link>
                      <Link to="/chat" className="li-btn-outline-primary" style={{ width: '100%', textAlign: 'center', display: 'block', boxSizing: 'border-box' }}>
                        {t('agentPerformance.updateTwin', 'Update AI Twin')}
                     </Link>
                </div>
             </div>
         </aside>
      </div>
    </div>
  );
}
export default AgentPerformance;
